/**
 * spotify-panel.js
 *
 * Spotify library browser — mirrors ApplePanel but uses SpotifyBridge.
 *
 * Auth flow differs from Apple Music: clicking "Connect Spotify" redirects
 * the browser to Spotify's auth page. On return, main.js handles the callback
 * and calls spotify.handleCallbackIfPresent() before this panel inits.
 */

import { SpotifyBridge } from './spotify-bridge.js';

export class SpotifyPanel {
  /**
   * @param {SpotifyBridge}      spotify
   * @param {import('webamp').default} webamp
   * @param {import('./webamp-controller').WebampController} ctrl
   */
  constructor(spotify, webamp, ctrl) {
    this.spotify = spotify;
    this.webamp  = webamp;
    this.ctrl    = ctrl;

    this._currentPlaylistId     = null;
    this._currentPlaylistTracks = [];
    this._searchDebounce        = null;
  }

  // ─────────────────────────────────────────────────────────────
  // Bootstrap
  // ─────────────────────────────────────────────────────────────

  init() {
    this._bindEvents();
    this._bindSpotifyEvents();

    // If already authorised (token restored from localStorage), skip auth screen
    if (this.spotify.isAuthorized) {
      this._showMainScreen();
      this._loadPlaylists();
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Events
  // ─────────────────────────────────────────────────────────────

  _bindEvents() {
    // Auth button — redirects to Spotify (PKCE)
    document.getElementById('amp-spotify-auth-btn').addEventListener('click', async () => {
      const btn = document.getElementById('amp-spotify-auth-btn');
      btn.textContent = 'Redirecting…';
      btn.disabled = true;
      await this.spotify.authorize(); // browser navigates away
    });

    // Disconnect button
    document.getElementById('amp-sp-disconnect-btn').addEventListener('click', () => {
      this.spotify.disconnect();
      this._showAuthScreen();
    });

    // Tab switching (Spotify-specific tabs)
    document.querySelectorAll('.amp-sp-tab').forEach((tab) => {
      tab.addEventListener('click', () => this._switchTab(tab.dataset.tab));
    });

    // Search
    const searchInput = document.getElementById('amp-sp-search-input');
    const searchBtn   = document.getElementById('amp-sp-search-btn');

    const doSearch = () => {
      clearTimeout(this._searchDebounce);
      this._searchDebounce = setTimeout(() => this._runSearch(searchInput.value), 400);
    };

    searchInput.addEventListener('input', doSearch);
    searchBtn.addEventListener('click', () => {
      clearTimeout(this._searchDebounce);
      this._runSearch(searchInput.value);
    });
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        clearTimeout(this._searchDebounce);
        this._runSearch(searchInput.value);
      }
    });
  }

  _bindSpotifyEvents() {
    this.spotify.addEventListener('trackchange', (e) => {
      this._updateNowPlaying(e.detail);
    });

    // Auto-return to auth screen if the token is cleared (e.g. 403 scope mismatch)
    this.spotify.addEventListener('disconnected', () => {
      this._showAuthScreen();
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Now Playing
  // ─────────────────────────────────────────────────────────────

  _updateNowPlaying(item) {
    if (!item) return;
    const attr = item.attributes ?? {};

    document.getElementById('amp-sp-track-title').textContent  = attr.name ?? '—';
    document.getElementById('amp-sp-track-artist').textContent = attr.artistName ?? '—';

    const artwork = document.getElementById('amp-sp-artwork');
    if (attr.artwork?.url) {
      artwork.src = attr.artwork.url; // Spotify URLs are already fully qualified
      artwork.style.display = 'block';
    } else {
      artwork.style.display = 'none';
    }

    document.getElementById('amp-sp-now-playing').classList.remove('amp-now-playing--hidden');

    document.querySelectorAll('.amp-list-item--active').forEach(el =>
      el.classList.remove('amp-list-item--active')
    );
    const activeEl = document.querySelector(`[data-track-id="${item.id}"]`);
    if (activeEl) activeEl.classList.add('amp-list-item--active');
  }

  // ─────────────────────────────────────────────────────────────
  // Playlists tab
  // ─────────────────────────────────────────────────────────────

  async _loadPlaylists() {
    const list = document.getElementById('amp-sp-playlist-list');
    list.innerHTML = '<li class="amp-list-item amp-list-item--loading">Loading…</li>';

    try {
      const playlists = await this.spotify.getLibraryPlaylists();

      list.innerHTML = '';

      const header = document.createElement('li');
      header.className = 'amp-list-item amp-list-header';
      header.textContent = 'Your Library';
      list.appendChild(header);

      if (!playlists.length) {
        const empty = document.createElement('li');
        empty.className = 'amp-list-item amp-list-item--empty';
        empty.textContent = 'No playlists found.';
        list.appendChild(empty);
        return;
      }

      playlists.forEach((playlist) => {
        list.appendChild(this._buildPlaylistItem(playlist));
      });

    } catch (err) {
      console.error('[SpotifyPanel] Failed to load playlists:', err);
      list.innerHTML = '<li class="amp-list-item amp-list-item--error">Failed to load playlists.</li>';
    }
  }

  _buildPlaylistItem(playlist) {
    const li = document.createElement('li');
    li.className = 'amp-list-item amp-list-item--playlist';
    li.dataset.playlistId = playlist.id;

    const artUrl = playlist.images?.[0]?.url ?? null;
    // February 2026: playlist field renamed tracks → items
    const trackCount = playlist.items?.total ?? playlist.tracks?.total ?? null;

    li.innerHTML = `
      ${artUrl
        ? `<img class="amp-item-art" src="${artUrl}" alt="" />`
        : '<span class="amp-item-art amp-item-art--placeholder">♫</span>'}
      <span class="amp-item-name">${this._esc(playlist.name ?? 'Untitled')}</span>
      <span class="amp-item-count">${trackCount != null ? trackCount + ' tracks' : ''}</span>
    `;

    li.addEventListener('click', () => this._onPlaylistClick(playlist, li));
    return li;
  }

  async _onPlaylistClick(playlist, listItem) {
    // Toggle sub-list
    const existing = listItem.nextElementSibling;
    if (existing?.classList.contains('amp-sublist')) {
      existing.remove();
      listItem.classList.remove('amp-list-item--expanded');
      return;
    }

    listItem.classList.add('amp-list-item--expanded');

    const subList = document.createElement('ul');
    subList.className = 'amp-sublist';
    subList.innerHTML = '<li class="amp-list-item amp-list-item--loading">Loading tracks…</li>';
    listItem.after(subList);

    try {
      const tracks = await this.spotify.getPlaylistTracks(playlist.id);

      subList.innerHTML = '';

      if (!tracks.length) {
        subList.innerHTML = '<li class="amp-list-item amp-list-item--empty">Playlist is empty.</li>';
        return;
      }

      this._currentPlaylistId     = playlist.id;
      this._currentPlaylistTracks = tracks;

      // "Play all" button
      const playAllLi = document.createElement('li');
      playAllLi.className = 'amp-list-item amp-list-item--play-all amp-sp-play-all';
      playAllLi.textContent = `▶ Play all (${tracks.length} tracks)`;
      playAllLi.addEventListener('click', (e) => {
        e.stopPropagation();
        this._loadPlaylistIntoWebamp(tracks, playlist.id, 0);
      });
      subList.appendChild(playAllLi);

      // Individual tracks
      tracks.forEach((track, index) => {
        const li = document.createElement('li');
        li.className = 'amp-list-item amp-list-item--track';
        li.dataset.trackId    = track.id;
        li.dataset.trackIndex = index;

        const dur = track.duration_ms
          ? this._formatTime(track.duration_ms / 1000) : '';

        li.innerHTML = `
          <span class="amp-track-num">${index + 1}</span>
          <span class="amp-track-name">${this._esc(track.name ?? '—')}</span>
          <span class="amp-track-dur">${dur}</span>
        `;

        li.addEventListener('click', (e) => {
          e.stopPropagation();
          this._loadPlaylistIntoWebamp(tracks, playlist.id, index);
        });

        subList.appendChild(li);
      });

    } catch (err) {
      console.error('[SpotifyPanel] Failed to load tracks:', err);
      const is403 = err?.message?.includes('403');
      const msg = is403
        ? 'This playlist is not accessible (private or restricted).'
        : 'Failed to load tracks.';
      subList.innerHTML = `<li class="amp-list-item amp-list-item--error">${msg}</li>`;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Search tab
  // ─────────────────────────────────────────────────────────────

  async _runSearch(query) {
    if (!query.trim()) return;

    const results = document.getElementById('amp-sp-search-results');
    results.innerHTML = '<li class="amp-list-item amp-list-item--loading">Searching…</li>';

    try {
      const { songs, albums } = await this.spotify.searchCatalog(query);
      results.innerHTML = '';

      if (!songs.length && !albums.length) {
        results.innerHTML = '<li class="amp-list-item amp-list-item--empty">No results found.</li>';
        return;
      }

      if (songs.length) {
        const h = document.createElement('li');
        h.className = 'amp-list-item amp-list-header';
        h.textContent = 'Songs';
        results.appendChild(h);

        songs.forEach((track) => {
          const artUrl = track.album?.images?.[0]?.url ?? null;
          const li = document.createElement('li');
          li.className = 'amp-list-item amp-list-item--track';
          li.dataset.trackId = track.id;

          li.innerHTML = `
            ${artUrl ? `<img class="amp-item-art" src="${artUrl}" alt="" />` : ''}
            <span class="amp-track-name">${this._esc(track.name ?? '—')}</span>
            <span class="amp-track-dur">${this._esc(track.artists?.[0]?.name ?? '')}</span>
          `;

          li.addEventListener('click', async () => {
            this.ctrl.setPlaylist([track], null);
            this.webamp.setTracksToPlay([SpotifyBridge.toWebampTrack(track)]);
            await this.spotify.playSong(track.id);
          });

          results.appendChild(li);
        });
      }

      if (albums.length) {
        const h = document.createElement('li');
        h.className = 'amp-list-item amp-list-header';
        h.textContent = 'Albums';
        results.appendChild(h);

        albums.forEach((album) => {
          const artUrl = album.images?.[0]?.url ?? null;
          const li = document.createElement('li');
          li.className = 'amp-list-item amp-list-item--playlist';

          li.innerHTML = `
            ${artUrl
              ? `<img class="amp-item-art" src="${artUrl}" alt="" />`
              : '<span class="amp-item-art amp-item-art--placeholder">💿</span>'}
            <span class="amp-item-name">${this._esc(album.name ?? '—')}</span>
            <span class="amp-item-count">${this._esc(album.artists?.[0]?.name ?? '')}</span>
          `;

          li.addEventListener('click', async () => {
            // Play album via Spotify API
            await this.spotify._apiCall(`/me/player/play?device_id=${this.spotify._deviceId}`, {
              method: 'PUT',
              body: JSON.stringify({ context_uri: `spotify:album:${album.id}` }),
            }).catch(console.warn);
          });

          results.appendChild(li);
        });
      }

    } catch (err) {
      console.error('[SpotifyPanel] Search failed:', err);
      results.innerHTML = '<li class="amp-list-item amp-list-item--error">Search failed.</li>';
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Webamp playlist sync
  // ─────────────────────────────────────────────────────────────

  _loadPlaylistIntoWebamp(tracks, playlistId, startIndex = 0) {
    this.ctrl.setPlaylist(tracks, playlistId);
    this.ctrl.pendingStartIndex = startIndex;
    this.webamp.setTracksToPlay(tracks.map(SpotifyBridge.toWebampTrack));
  }

  // ─────────────────────────────────────────────────────────────
  // Visibility helpers
  // ─────────────────────────────────────────────────────────────

  _showMainScreen() {
    document.getElementById('amp-spotify-auth-screen').classList.add('amp-screen--hidden');
    document.getElementById('amp-spotify-main-screen').classList.remove('amp-screen--hidden');
  }

  _showAuthScreen() {
    document.getElementById('amp-spotify-main-screen').classList.add('amp-screen--hidden');
    document.getElementById('amp-spotify-auth-screen').classList.remove('amp-screen--hidden');
    const btn = document.getElementById('amp-spotify-auth-btn');
    btn.textContent = 'Connect Spotify';
    btn.disabled = false;
    // Clear playlist list so it reloads fresh on next sign-in
    document.getElementById('amp-sp-playlist-list').innerHTML = '';
  }

  _switchTab(tabName) {
    document.querySelectorAll('.amp-sp-tab').forEach((t) => {
      t.classList.toggle('amp-tab--active', t.dataset.tab === tabName);
    });
    const playlistsEl = document.getElementById('amp-sp-tab-playlists');
    const searchEl    = document.getElementById('amp-sp-tab-search');
    playlistsEl.classList.toggle('amp-tab-content--hidden', tabName !== 'playlists');
    searchEl.classList.toggle('amp-tab-content--hidden',    tabName !== 'search');
  }

  // ─────────────────────────────────────────────────────────────
  // Utilities
  // ─────────────────────────────────────────────────────────────

  _formatTime(secs) {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
